import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Button, ListItemIcon, Menu, MenuItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useVisibleCount from "./useVisibleCount";

const LINK_GAP = 8;

/**
 * @param {string} linkPath - a link target, possibly with a query string
 * @param {string} pathname - the current location's pathname
 */
export const isHeaderLinkActive = (linkPath, pathname) => {
  const target = String(linkPath || "").split("?")[0];
  if (!target || target === "/") return pathname === target;
  return pathname === target || pathname.startsWith(`${target}/`);
};

const linkButtonSx = (isActive) => ({
  flex: "0 0 auto",
  color: isActive ? "primary.main" : "common.white",
  backgroundColor: isActive ? "common.white" : "transparent",
  textTransform: "none",
  fontWeight: isActive ? 750 : 550,
  fontSize: "1rem",
  px: 2,
  py: 0.75,
  height: "40px",
  minWidth: "auto",
  whiteSpace: "nowrap",
  borderRadius: "999px",
  transition: "background-color 0.2s ease, color 0.2s ease",
  "& .MuiButton-startIcon": { color: isActive ? "primary.main" : "common.white", marginRight: "6px" },
  "& .MuiButton-endIcon": { marginLeft: "2px" },
  "&:hover": { backgroundColor: isActive ? "common.white" : "rgba(255,255,255,0.15)" },
  // Gloved use on a tablet.
  "@media (pointer: coarse)": { height: "44px" },
});

/**
 * The header's page links on one row. Links that do not fit move into a "More" menu, so none
 * wraps or is cut off; the active page always stays on the row.
 *
 * @param {{ links: { label: string, path: string, icon: React.ElementType }[] }} props
 */
export default function HeaderNavLinks({ links }) {
  const { pathname } = useLocation();
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const visibleCount = useVisibleCount(containerRef, measureRef, links.length, LINK_GAP);

  const activeIndex = links.findIndex((link) => isHeaderLinkActive(link.path, pathname));
  const visibleIndexes = links.map((_, index) => index).slice(0, visibleCount);
  if (activeIndex >= visibleCount && visibleCount > 0) {
    visibleIndexes[visibleCount - 1] = activeIndex;
  }
  const overflowLinks = links.filter((_, index) => !visibleIndexes.includes(index));
  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box
      ref={containerRef}
      component="nav"
      aria-label="Main"
      sx={{ position: "relative", flex: "1 1 0", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: `${LINK_GAP}px`, overflow: "hidden" }}
    >
      {visibleIndexes.map((index) => {
        const { label, path, icon: Icon } = links[index];
        const isActive = index === activeIndex;
        return (
          <Button
            key={label}
            component={Link}
            to={path}
            aria-current={isActive ? "page" : undefined}
            startIcon={<Icon sx={{ fontSize: 20 }} />}
            sx={linkButtonSx(isActive)}
          >
            {label}
          </Button>
        );
      })}

      {overflowLinks.length ? (
        <>
          <Button
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={linkButtonSx(false)}
          >
            More
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {overflowLinks.map(({ label, path, icon: Icon }) => (
              <MenuItem key={label} component={Link} to={path} onClick={closeMenu} sx={{ minHeight: 48, fontSize: "1rem" }}>
                <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                {label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      {/* Hidden copy used only to measure each link (and "More") at its full width. */}
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{ position: "absolute", top: 0, left: 0, display: "flex", visibility: "hidden", pointerEvents: "none", height: 0, overflow: "hidden" }}
      >
        {links.map(({ label, icon: Icon }) => (
          <Button key={label} tabIndex={-1} startIcon={<Icon sx={{ fontSize: 20 }} />} sx={linkButtonSx(true)}>
            {label}
          </Button>
        ))}
        <Button tabIndex={-1} endIcon={<ExpandMoreIcon />} sx={linkButtonSx(false)}>More</Button>
      </Box>
    </Box>
  );
}
